import React from 'react'
import { LABOR_BURDEN_RATE, detectType, itemTrueCost } from '../../lib/estimateFinancials'

function MetricBlock({ label, value, sub, color }) {
  return (
    <div style={mt.metricBlock}>
      <div style={mt.metricLabel}>{label}</div>
      <div style={{ ...mt.metricValue, color: color || '#1a2e1a' }}>{value}</div>
      {sub && <div style={mt.metricSub}>{sub}</div>}
    </div>
  )
}

export default function MetricsTab({ sections, grandTotal, tax, trueCost, costByType, overheadTotal, overheadPerLaborHour, totalLaborHours, loadedLaborCost: loadedLaborCostProp, estimatedHours, loadedLaborRate }) {
  const grossProfit = grandTotal - trueCost
  const grossMargin = grandTotal > 0 ? (grossProfit / grandTotal * 100) : 0
  const netProfit = grandTotal - trueCost
  const netMargin = grandTotal > 0 ? (netProfit / grandTotal * 100) : 0
  const breakEven = trueCost
  const contingency5 = grandTotal * 0.05
  const contingency10 = grandTotal * 0.10
  const revenuePerCrewHr = estimatedHours > 0 ? grandTotal / estimatedHours : 0
  const laborBurden = costByType.Labor * LABOR_BURDEN_RATE
  // Loaded labor = base labor + overhead spread across labor hours
  const loadedLaborCost = loadedLaborCostProp || (costByType.Labor + overheadTotal)
  const effectiveLaborRate = totalLaborHours > 0 ? loadedLaborCost / totalLaborHours : 0
  const materialPct = trueCost > 0 ? (costByType.Material / trueCost * 100) : 0
  const laborPct = trueCost > 0 ? (costByType.Labor / trueCost * 100) : 0
  const overheadPct = grandTotal > 0 ? (overheadTotal / grandTotal * 100) : 0
  const markupApplied = grandTotal - trueCost - tax

  // Traffic light
  const health = netMargin >= 35 ? 'green' : netMargin >= 20 ? 'yellow' : 'red'
  const healthColors = { green: { bg: '#dcfce7', color: '#15803d', label: 'Healthy' }, yellow: { bg: '#fef9c3', color: '#92400e', label: 'Watch Margin' }, red: { bg: '#fee2e2', color: '#dc2626', label: 'High Risk' } }
  const hc = healthColors[health]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {/* Job Health */}
      <div style={{ background: hc.bg, border: `2px solid ${hc.color}`, borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ fontSize: '28px' }}>{health === 'green' ? '🟢' : health === 'yellow' ? '🟡' : '🔴'}</div>
        <div>
          <div style={{ fontWeight: '700', fontSize: '16px', color: hc.color }}>Job Health: {hc.label}</div>
          <div style={{ fontSize: '13px', color: hc.color, opacity: 0.85 }}>
            Net margin: {netMargin.toFixed(1)}% · {health === 'green' ? 'Solid margin — good to go.' : health === 'yellow' ? 'Margin is thin — check labor and overhead.' : 'Margin below 10% — high risk for landscaping.'}
          </div>
        </div>
      </div>

      {/* Cost breakdown */}
      <div style={mt.card}>
        <div style={mt.cardTitle}>Cost Breakdown</div>
        <div style={mt.grid4}>
          <MetricBlock label="Materials" value={`$${costByType.Material.toFixed(2)}`} sub={`${materialPct.toFixed(1)}% of cost`} color="#0369a1" />
          <MetricBlock label="Labor (base)" value={`$${costByType.Labor.toFixed(2)}`} sub={`${laborPct.toFixed(1)}% of cost`} color="#15803d" />
          <MetricBlock label="Equipment" value={`$${costByType.Equipment.toFixed(2)}`} sub={`${trueCost > 0 ? (costByType.Equipment / trueCost * 100).toFixed(1) : '0'}% of cost`} color="#92400e" />
          <MetricBlock label="Overhead" value={`$${overheadTotal.toFixed(2)}`} sub={`${overheadPct.toFixed(1)}% of revenue`} color="#7c3aed" />
        </div>
        <div style={{ marginTop: '12px', padding: '10px 14px', background: '#f9fafb', borderRadius: '8px', fontSize: '13px', color: '#6b7c6b' }}>
          Labor-to-Materials ratio: <strong style={{ color: '#1a2e1a' }}>{costByType.Material > 0 ? (costByType.Labor / costByType.Material).toFixed(2) : '—'} : 1</strong>
          {' '}· Industry typical for landscaping: <strong>0.5–1.0 : 1</strong>
        </div>
      </div>

      {/* Profitability */}
      <div style={mt.card}>
        <div style={mt.cardTitle}>Profitability</div>
        <div style={mt.grid4}>
          <MetricBlock label="Contract Total" value={`$${grandTotal.toFixed(2)}`} color="#1a2e1a" />
          <MetricBlock label="True Job Cost" value={`$${trueCost.toFixed(2)}`} color="#374151" />
          <MetricBlock label="Gross Profit" value={`$${grossProfit.toFixed(2)}`} sub={`${grossMargin.toFixed(1)}% gross margin`} color={grossProfit >= 0 ? '#15803d' : '#dc2626'} />
          <MetricBlock label="Net Profit" value={`$${netProfit.toFixed(2)}`} sub={`${netMargin.toFixed(1)}% net margin`} color={netProfit >= 0 ? '#15803d' : '#dc2626'} />
        </div>
        <div style={mt.grid2mt}>
          <MetricBlock label="Markup Applied" value={`$${markupApplied.toFixed(2)}`} sub="above cost" color="#374151" />
          <MetricBlock label="Break-Even Point" value={`$${breakEven.toFixed(2)}`} sub="must exceed to profit" color="#7c3aed" />
        </div>
      </div>

      {/* Safety Net */}
      <div style={mt.card}>
        <div style={mt.cardTitle}>Safety Net & Risk</div>
        <div style={mt.grid4}>
          <MetricBlock label="Contingency (5%)" value={`$${contingency5.toFixed(2)}`} sub="buffer for unknowns" color="#0369a1" />
          <MetricBlock label="Contingency (10%)" value={`$${contingency10.toFixed(2)}`} sub="high-risk buffer" color="#0369a1" />
          <MetricBlock label="Labor Burden (25%)" value={`$${laborBurden.toFixed(2)}`} sub="taxes + comp + benefits" color="#92400e" />
          <MetricBlock label="Loaded Labor Cost" value={`$${loadedLaborCost.toFixed(2)}`} sub="true labor incl. burden" color="#92400e" />
        </div>
      </div>

      {/* Overhead Spread by Section */}
      {overheadTotal > 0 && totalLaborHours > 0 && (
        <div style={mt.card}>
          <div style={mt.cardTitle}>Overhead Spread Across Labor</div>
          <div style={{ fontSize: '13px', color: '#6b7c6b', marginBottom: '14px', lineHeight: '1.5' }}>
            Total overhead <strong style={{ color: '#7c3aed' }}>${overheadTotal.toFixed(2)}</strong> ÷ {totalLaborHours} labor hours = <strong style={{ color: '#7c3aed' }}>${overheadPerLaborHour.toFixed(2)}/hr</strong> added to each labor hour.
            Client price is unchanged — this is internal job costing only.
          </div>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', padding: '8px 12px', background: '#f9fafb', fontSize: '10px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <span style={{ flex: 2 }}>Section</span>
              <span style={{ flex: 1, textAlign: 'center' }}>Labor Hrs</span>
              <span style={{ flex: 1, textAlign: 'right' }}>Base Labor $</span>
              <span style={{ flex: 1, textAlign: 'right' }}>Overhead Add</span>
              <span style={{ flex: 1, textAlign: 'right' }}>Loaded Labor $</span>
            </div>
            {sections.filter(s => !s.is_overhead).map(sec => {
              const secLaborItems = sec.items.filter(i => detectType(i, sec.name) === 'Labor')
              const secLaborHrs = secLaborItems.reduce((sum, i) => sum + parseFloat(i.qty || 0), 0)
              const secLaborCost = secLaborItems.reduce((sum, i) => sum + itemTrueCost(i), 0)
              const secOverheadAdd = secLaborHrs * overheadPerLaborHour
              if (secLaborHrs === 0) return null
              return (
                <div key={sec._id} style={{ display: 'flex', padding: '10px 12px', borderTop: '1px solid #f3f4f6', fontSize: '13px', alignItems: 'center' }}>
                  <span style={{ flex: 2, fontWeight: '500', color: '#1a2e1a' }}>{sec.name}</span>
                  <span style={{ flex: 1, textAlign: 'center', color: '#6b7c6b' }}>{secLaborHrs} hrs</span>
                  <span style={{ flex: 1, textAlign: 'right', color: '#374151' }}>${secLaborCost.toFixed(2)}</span>
                  <span style={{ flex: 1, textAlign: 'right', color: '#7c3aed', fontWeight: '600' }}>+${secOverheadAdd.toFixed(2)}</span>
                  <span style={{ flex: 1, textAlign: 'right', fontWeight: '700', color: '#1a2e1a' }}>${(secLaborCost + secOverheadAdd).toFixed(2)}</span>
                </div>
              )
            })}
            <div style={{ display: 'flex', padding: '10px 12px', borderTop: '2px solid #e5e7eb', fontSize: '13px', fontWeight: '700', background: '#f9fafb' }}>
              <span style={{ flex: 2, color: '#1a2e1a' }}>Total</span>
              <span style={{ flex: 1, textAlign: 'center', color: '#374151' }}>{totalLaborHours} hrs</span>
              <span style={{ flex: 1, textAlign: 'right', color: '#374151' }}>${costByType.Labor.toFixed(2)}</span>
              <span style={{ flex: 1, textAlign: 'right', color: '#7c3aed' }}>+${overheadTotal.toFixed(2)}</span>
              <span style={{ flex: 1, textAlign: 'right', color: '#1a2e1a' }}>${loadedLaborCost.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Efficiency */}
      <div style={mt.card}>
        <div style={mt.cardTitle}>Efficiency & Production</div>
        <div style={mt.grid2}>
          <div>
            <label style={mt.inputLabel}>Estimated Total Crew Hours</label>
            <div style={{ fontSize: '13px', color: '#6b7c6b', marginBottom: '8px' }}>Enter in the estimate to calculate revenue per crew hour</div>
            <MetricBlock label="Revenue per Crew Hour" value={estimatedHours > 0 ? `$${revenuePerCrewHr.toFixed(2)}/hr` : '— enter hours'} sub="target: $75–150/hr for landscaping" color={revenuePerCrewHr >= 75 ? '#15803d' : revenuePerCrewHr > 0 ? '#92400e' : '#9ca3af'} />
          </div>
          <div>
            <MetricBlock label="Effective Labor Rate (w/ overhead)" value={totalLaborHours > 0 ? `$${effectiveLaborRate.toFixed(2)}/hr` : '—'} sub={`base $${totalLaborHours > 0 ? (costByType.Labor/totalLaborHours).toFixed(2) : '0'}/hr + $${overheadPerLaborHour.toFixed(2)}/hr overhead`} color="#7c3aed" />
            <div style={{ marginTop: '12px' }}>
              <MetricBlock label="Material Waste Factor (10%)" value={`$${(costByType.Material * 0.10).toFixed(2)}`} sub="recommended buffer on materials" color="#0369a1" />
            </div>
            <div style={{ marginTop: '12px' }}>
              <MetricBlock label="Overhead as % of Revenue" value={`${overheadPct.toFixed(1)}%`} sub="industry target: 15–25%" color={overheadPct <= 25 ? '#15803d' : '#dc2626'} />
            </div>
          </div>
        </div>
      </div>

      {/* Pricing scenarios */}
      <div style={mt.card}>
        <div style={mt.cardTitle}>Pricing Scenarios</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Scenario</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Client Price</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Gross Profit</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Margin</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: '10% discount', price: grandTotal * 0.90 },
                { label: 'Current quote', price: grandTotal, highlight: true },
                { label: '10% higher', price: grandTotal * 1.10 },
                { label: '20% higher', price: grandTotal * 1.20 },
              ].map(row => {
                const gp = row.price - trueCost
                const gm = row.price > 0 ? (gp / row.price * 100) : 0
                return (
                  <tr key={row.label} style={{ background: row.highlight ? '#f2faf2' : 'white', borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', fontWeight: row.highlight ? '700' : '400', color: row.highlight ? '#1a2e1a' : '#374151' }}>{row.label}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: row.highlight ? '700' : '400' }}>${row.price.toFixed(2)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: gp >= 0 ? '#15803d' : '#dc2626' }}>${gp.toFixed(2)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: gm >= 35 ? '#15803d' : gm >= 20 ? '#92400e' : '#dc2626', fontWeight: '600' }}>{gm.toFixed(1)}%</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

const mt = {
  card: { background: 'white', border: '1px solid #e4ebe4', borderRadius: '12px', padding: '20px 22px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  cardTitle: { fontSize: '14px', fontWeight: '700', color: '#1a2e1a', marginBottom: '14px', paddingBottom: '10px', borderBottom: '1px solid #f0f0f0' },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  grid2mt: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' },
  metricBlock: { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px' },
  metricLabel: { fontSize: '10px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
  metricValue: { fontSize: '18px', fontWeight: '700', lineHeight: 1.2 },
  metricSub: { fontSize: '11px', color: '#9ca3af', marginTop: '3px' },
  inputLabel: { display: 'block', fontSize: '11px', fontWeight: '600', color: '#374151', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
}
